/**
 * /policies/payroll/gosi-rates — Session 3c PR 17.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { GosiRateItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function PayrollGosiRatesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-payroll-gosi", le],
    queryFn: () => session3cApi.listGosiRates(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<GosiRateItem>[] = [
    {
      headerKey: "policies.payroll_gosi.col.category",
      label: "Category",
      render: (r) => <span className="text-xs uppercase">{r.employee_category}</span>,
    },
    {
      headerKey: "policies.payroll_gosi.col.type",
      label: "Contribution",
      render: (r) => <span className="text-xs">{r.contribution_type}</span>,
    },
    {
      headerKey: "policies.payroll_gosi.col.employee",
      label: "Employee %",
      render: (r) => <span className="font-mono">{r.employee_rate_pct.toFixed(1)}%</span>,
    },
    {
      headerKey: "policies.payroll_gosi.col.employer",
      label: "Employer %",
      render: (r) => <span className="font-mono">{r.employer_rate_pct.toFixed(1)}%</span>,
    },
    {
      headerKey: "policies.payroll_gosi.col.source",
      label: "Source",
      render: (r) => <span className="text-[10px] text-text-tertiary">{r.source}</span>,
    },
  ];

  return (
    <PolicyListPage<GosiRateItem>
      title={t("policies.payroll_gosi.title", "GOSI Rate Tables")}
      subtitle={t("policies.payroll_gosi.subtitle", "Saudi vs non-Saudi schedule per Article M/33. Locked to statutory schedule — view-only.")}
      agent="mutabaa"
      domain="payroll_gosi"
      newButtonLabel={t("policies.payroll_gosi.cta.new", "Locked (statutory)")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => `${r.employee_category}-${r.contribution_type}`}
      suggestions={[
        {
          headline: t("policies.payroll_gosi.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.payroll_gosi.suggestion.clean_body", "GOSI rates are statutory — cannot be overridden. The 'Refresh from GOSI portal' button ships with the M3X.GOV pack."),
          primaryActionLabel: t("policies.payroll_gosi.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "payroll-gosi-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.payroll_gosi.title", "GOSI Rate Tables"),
        window_label: t("policies.payroll_gosi.window", "Article M/33 schedule"),
      }}
    />
  );
}
